import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { FieldErrors } from "react-hook-form";
import { Button } from "@/components/ui/button";
import Form from "@/components/Form";
import { WriteQuestionnaire } from "@/types/questionnaire";
import { QuestionnaireDataSection } from "../sections/QuestionnaireDataSection";
import { QuestionnaireStatementsSection } from "../sections/QuestionnaireStatementsSection";
import { QuestionnaireAttachmentsSection } from "../sections/QuestionnaireAttachmentsSection";
import { QuestionnaireRegulationsSection } from "../sections/QuestionnaireRegulationsSection";
import { QuestionnaireRodoSection } from "../sections/QuestionnaireRodoSection";
import { useQuestionnaireContext } from "../../context/QuestionnaireContext";
import { useQuestionnaireData, useQuestionnaireForm } from "../../hooks";
import { useToast } from "@/components/ui/use-toast";
import { getEmploymentCertificateWarningTranslationKey } from "../sections/utils/getEmploymentCertificateWarningTranslationKey";

export function QuestionnaireStep() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { handleSubmitForm, handleSubmitFormWithoutConfirm, presignedClient } =
    useQuestionnaireContext();
  const {
    questionnaire,
    attachments,
    agreementStatus,
    showRodoSection,
    showRegulationsSection,
  } = useQuestionnaireData();

  const isUpdateContentMode = agreementStatus === "agreement_update_content";
  const isAmendStatements =
    questionnaire.agreement_status === "agreement_amend_statements";

  const form = useQuestionnaireForm(
    questionnaire,
    isUpdateContentMode,
    isAmendStatements,
  );

  const handleFormInvalid = useCallback(
    (errors: FieldErrors<WriteQuestionnaire>) => {
      console.error("[QuestionnaireValidation]", {
        validationErrors: errors,
        formValues: form.getValues(),
      });
      toast({
        title: t("questionnaires.errors.validationTitle"),
        description: t("questionnaires.errors.validationDescription"),
        variant: "destructive",
      });
    },
    [t, toast, form],
  );

  const sections = useMemo(() => {
    if (isAmendStatements) {
      return [
        { key: "rodo", element: <QuestionnaireRodoSection /> },
        { key: "regulations", element: <QuestionnaireRegulationsSection /> },
      ];
    }

    const list: { key: string; element: React.ReactNode }[] = [];

    if (showRodoSection) {
      list.push({
        key: "rodo",
        element: <QuestionnaireRodoSection />,
      });
    }

    if (showRegulationsSection) {
      list.push({
        key: "regulations",
        element: <QuestionnaireRegulationsSection />,
      });
    }

    list.push({
      key: "data",
      element: <QuestionnaireDataSection disabled={isUpdateContentMode} />,
    });

    list.push({
      key: "statements",
      element: (
        <QuestionnaireStatementsSection
          questionnaire={questionnaire}
          attachments={attachments}
          disabled={isUpdateContentMode}
        />
      ),
    });

    list.push({
      key: "attachments",
      element: (
        <QuestionnaireAttachmentsSection
          attachments={attachments}
          disabled={isUpdateContentMode}
        />
      ),
    });

    return list;
  }, [
    attachments,
    questionnaire.agreement_status,
    isUpdateContentMode,
    isAmendStatements,
    showRodoSection,
    showRegulationsSection,
  ]);

  const handleFormSubmit = useCallback(
    (data: WriteQuestionnaire) => {
      const warningTranslationKey =
        getEmploymentCertificateWarningTranslationKey({
          isEmployed: data.is_employed,
          issueDate: data.employment_certificate_issue_date,
          existingAttachments: attachments.employment_certificate_attachments,
          newAttachments: data.employment_certificate_attachments,
        });

      const warningDescription = warningTranslationKey
        ? t(warningTranslationKey)
        : null;

      handleSubmitForm(data, warningDescription);
    },
    [attachments, handleSubmitForm, t],
  );

  const handleReadonlySubmit = useCallback(() => {
    const formData = form.getValues();
    handleSubmitFormWithoutConfirm(formData);
  }, [form, handleSubmitFormWithoutConfirm]);

  return (
    <div className="min-h-screen bg-white">
      <section className="mx-auto w-full max-w-4xl border-x border-slate-200 bg-white">
        <Form
          form={form}
          handleSubmit={
            isUpdateContentMode || isAmendStatements
              ? handleReadonlySubmit
              : handleFormSubmit
          }
          onValidationError={handleFormInvalid}
          className="flex flex-col gap-0 px-4 py-6 sm:px-6 sm:py-8"
        >
          <div className="flex flex-col divide-y divide-slate-200">
            {sections.map((section) => (
              <div key={section.key} className="py-6 first:pt-0 last:pb-0">
                {section.element}
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2 pt-6 sm:flex-row sm:justify-end">
            <Button
              type="submit"
              className="w-full sm:w-auto"
              disabled={
                !isUpdateContentMode &&
                (presignedClient.isUploading || presignedClient.isRemoving)
              }
            >
              {isUpdateContentMode
                ? t("questionnaires.form.continueButton")
                : isAmendStatements
                  ? t("questionnaires.form.submitButtonStatements")
                  : t("questionnaires.form.submitButton")}
            </Button>
          </div>
        </Form>
      </section>
    </div>
  );
}
